import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { TuiHint, TuiLoader } from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';

export type CountListBadgeLoad = () => Promise<string[]>;

@Component({
	selector: 'mar-count-list-badge',
	standalone: true,
	imports: [TuiBadge, TuiHint, TuiLoader, TranslocoPipe],
	templateUrl: 'count-list-badge.component.html',
	host: { class: 'inline-flex' },
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountListBadgeComponent {
	readonly items = input<string[]>([]);
	readonly count = input<number | undefined>(undefined);
	readonly load = input<CountListBadgeLoad | undefined>();

	readonly loading = signal(false);
	private readonly fetchedItems = signal<string[] | null>(null);

	readonly resolvedItems = computed(
		() => this.fetchedItems() ?? this.items(),
	);

	readonly displayedCount = computed(
		() => this.count() ?? this.resolvedItems().length,
	);

	async ensureLoaded() {
		if (this.fetchedItems() !== null) return;
		if (this.items().length) return;

		const load = this.load();
		if (!load) return;
		if (this.loading()) return;
		if (!this.displayedCount()) {
			this.fetchedItems.set([]);
			return;
		}

		this.loading.set(true);
		try {
			this.fetchedItems.set(await load());
		} finally {
			this.loading.set(false);
		}
	}
}
